(() => {
  const initHomeHeader = () => {
    if (!document.body.classList.contains('template-index')) return;
    const stickyHeader = document.querySelector('sticky-header');
    const heroSection = document.querySelector('#MainContent > .shopify-section:first-child');
    if (!stickyHeader || !heroSection) return;

    const mobileHeader = window.matchMedia('(max-width: 749px)');
    const update = () => {
      /* Keep the mobile Home hero below the header, not behind it. */
      if (mobileHeader.matches) {
        stickyHeader.classList.remove('header-transparent');
        stickyHeader.style.marginBottom = '';
        return;
      }

      const headerBottom = stickyHeader.getBoundingClientRect().bottom;
      const heroBottom = heroSection.getBoundingClientRect().bottom;
      const overlapsHero = heroBottom > headerBottom;

      stickyHeader.classList.toggle('header-transparent', overlapsHero);
      stickyHeader.style.marginBottom = `${-stickyHeader.offsetHeight}px`;
    };

    let updateQueued = false;
    const queueUpdate = () => {
      if (updateQueued) return;
      updateQueued = true;
      window.requestAnimationFrame(() => {
        updateQueued = false;
        update();
      });
    };

    update();
    window.addEventListener('scroll', queueUpdate, { passive: true });
    window.addEventListener('resize', queueUpdate, { passive: true });
  };

  const initFooterSubmenus = () => {
    const mobileMedia = window.matchMedia('(max-width: 749px)');
    const openClass = 'etremiu-footer-nav--open';

    document.querySelectorAll('.footer-block__nav > li > span').forEach((trigger) => {
      const syncAccessibility = () => {
        if (mobileMedia.matches) {
          trigger.setAttribute('role', 'button');
          trigger.setAttribute('tabindex', '0');
          trigger.setAttribute('aria-expanded', trigger.classList.contains(openClass) ? 'true' : 'false');
        } else {
          trigger.classList.remove(openClass);
          trigger.removeAttribute('role');
          trigger.removeAttribute('tabindex');
          trigger.removeAttribute('aria-expanded');
        }
      };

      const toggle = () => {
        if (!mobileMedia.matches) return;
        const expanded = trigger.classList.toggle(openClass);
        trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      };

      trigger.addEventListener('click', toggle);
      trigger.addEventListener('keydown', (event) => {
        if (!mobileMedia.matches) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          toggle();
        }
      });

      mobileMedia.addEventListener('change', syncAccessibility);
      syncAccessibility();
    });
  };

  const initVariantItemNumbers = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const sectionId = event?.data?.sectionId;
      const html = event?.data?.html;
      if (!sectionId || !html) return;

      const source = html.getElementById(`VariantItemNumber-${sectionId}`);
      const target = document.getElementById(`VariantItemNumber-${sectionId}`);
      if (!source || !target) return;

      target.innerHTML = source.innerHTML;
      target.classList.toggle('visibility-hidden', source.classList.contains('visibility-hidden'));
    });
  };

  const initWishlistHeaderLink = () => {
    const cartLink = document.getElementById('cart-icon-bubble');
    if (!cartLink || document.getElementById('etremiu-wishlist-link')) return;

    const wishlistLink = document.createElement('a');
    wishlistLink.id = 'etremiu-wishlist-link';
    wishlistLink.href = '/apps/wishlist';
    wishlistLink.className = 'header__icon header__icon--wishlist link focus-inset';
    wishlistLink.setAttribute('aria-label', 'Wishlist');
    if (window.location.pathname.startsWith('/apps/wishlist')) {
      wishlistLink.setAttribute('aria-current', 'page');
    }
    wishlistLink.innerHTML = `
      <span class="svg-wrapper" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-heart" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" focusable="false">
          <path d="M17.366 3.841a4.5 4.5 0 0 0-6.364 0L10 4.843 8.998 3.841a4.5 4.5 0 1 0-6.364 6.364L10 17.571l7.366-7.366a4.5 4.5 0 0 0 0-6.364Z"/>
        </svg>
      </span>`;

    cartLink.before(wishlistLink);
  };

  const initVariantPriceUpdateOptimization = () => {
    const ProductInfo = customElements.get('product-info');
    if (!ProductInfo || ProductInfo.prototype.etremiuPriceUpdateOptimized) return;

    const sameUnitMeasurement = (currentVariant, nextVariant) =>
      JSON.stringify(currentVariant?.unit_price_measurement ?? null) ===
      JSON.stringify(nextVariant?.unit_price_measurement ?? null);

    const canPreservePrice = (productInfo, currentVariant, nextVariant) => {
      if (!currentVariant || !nextVariant || currentVariant.id === nextVariant.id) return false;
      if (productInfo.querySelector('[id^="Volume-"]')) return false;

      return (
        currentVariant.price === nextVariant.price &&
        currentVariant.compare_at_price === nextVariant.compare_at_price &&
        currentVariant.unit_price === nextVariant.unit_price &&
        sameUnitMeasurement(currentVariant, nextVariant)
      );
    };

    ProductInfo.prototype.handleUpdateProductInfo = function (productUrl) {
      return (html) => {
        const currentVariant = this.getSelectedVariant(this);
        const sourceVariantSelects = this.getVariantSelects(html);
        const variant = this.getSelectedVariant(html);
        const preservePrice = canPreservePrice(this, currentVariant, variant);

        this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

        this.pickupAvailability?.update(variant);
        this.updateOptionValues(html);
        this.updateURL(productUrl, variant?.id);
        this.updateVariantInputs(variant?.id);

        if (!variant) {
          this.setUnavailable();
          return;
        }

        this.updateMedia(html, variant?.featured_media?.id);

        const updateSourceFromDestination = (id, shouldHide = () => false) => {
          const source = html.getElementById(`${id}-${this.sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.section}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('hidden', shouldHide(source));
          }
        };

        const syncPreservedPriceState = () => {
          const source = html.getElementById(`price-${this.sectionId}`);
          const destination = this.querySelector(`#price-${this.dataset.section}`);
          const sourcePrice = source?.querySelector('.price');
          const destinationPrice = destination?.querySelector('.price');
          if (!sourcePrice || !destinationPrice) return;

          ['price--sold-out', 'price--unavailable'].forEach((className) => {
            destinationPrice.classList.toggle(className, sourcePrice.classList.contains(className));
          });
        };

        if (preservePrice) {
          syncPreservedPriceState();
        } else {
          updateSourceFromDestination('price');
        }
        updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
        updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
        updateSourceFromDestination('Volume');
        updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

        this.updateQuantityRules(this.sectionId, html);
        this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
        this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

        this.productForm?.toggleSubmitButton(
          html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
          window.variantStrings.soldOut
        );

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.sectionId,
            html,
            variant,
          },
        });
      };
    };

    ProductInfo.prototype.etremiuPriceUpdateOptimized = true;
  };

  document.addEventListener('DOMContentLoaded', () => {
    initHomeHeader();
    initFooterSubmenus();
    initVariantPriceUpdateOptimization();
    initVariantItemNumbers();
    initWishlistHeaderLink();
  });
})();
